import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, gbp, Loading } from "@/components/ui-bits";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import CredentialsDialog from "@/components/CredentialsDialog";
import { Users, Plus, X, Building2, Loader2, Save, Search, FileDown, Trash2, Archive, RotateCcw, Landmark, Pencil, CreditCard, KeyRound, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Landlords() {
  const [data, setData] = useState(null);
  const [addLL, setAddLL] = useState(false);
  const [addProp, setAddProp] = useState(null); // landlord for new property
  const [llForm, setLlForm] = useState({ nickname: "", name: "", email: "", phone: "" });
  const [creds, setCreds] = useState(null);
  const [resetBusy, setResetBusy] = useState(null);
  const [propForm, setPropForm] = useState({ registered_name: "", reference: "", address: "", postcode: "", city: "", beds: 2, baths: 1, rent_pcm: "" });
  const [fees, setFees] = useState({});
  const [busy, setBusy] = useState(false);
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [stmtBusy, setStmtBusy] = useState(null);
  const [emailBusy, setEmailBusy] = useState(null);
  const [selProps, setSelProps] = useState({});   // landlord_id -> [property_id]
  const [stmtDates, setStmtDates] = useState({});  // landlord_id -> {from, to}
  const [combBusy, setCombBusy] = useState(null);
  const [combEmailBusy, setCombEmailBusy] = useState(null);
  const emptyBank = { label: "", account_name: "", sort_code: "", account_number: "", notes: "" };
  const [bankModal, setBankModal] = useState(null);   // { landlord, account? }
  const [bankForm, setBankForm] = useState(emptyBank);
  const [savingBank, setSavingBank] = useState(false);
  const [payoutBusy, setPayoutBusy] = useState(null);
  const emptyEdit = { reference: "", address: "", postcode: "", city: "", beds: "", baths: "", rent_pcm: "", landlord_user_id: "" };
  const [propEdit, setPropEdit] = useState(null);     // property being edited
  const [editForm, setEditForm] = useState(emptyEdit);
  const [savingEdit, setSavingEdit] = useState(false);

  const showBankWarning = (res) => {
    const w = res?.headers?.["x-bank-warning"];
    if (w) toast.warning(decodeURIComponent(w), { duration: 9000 });
  };

  const openBank = (landlord, account = null) => {
    setBankForm(account ? { label: account.label || "", account_name: account.account_name || "", sort_code: account.sort_code || "", account_number: account.account_number || "", notes: account.notes || "" } : emptyBank);
    setBankModal({ landlord, account });
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      const { landlord, account } = bankModal;
      if (account) await api.patch(`/landlords/${landlord.user_id}/bank-accounts/${account.account_id}`, bankForm);
      else await api.post(`/landlords/${landlord.user_id}/bank-accounts`, bankForm);
      toast.success(account ? "Bank account updated" : "Bank account added");
      setBankModal(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save bank account"); }
    finally { setSavingBank(false); }
  };

  const deleteBank = async (landlord, account) => {
    if (!window.confirm(`Delete bank account "${account.label || account.account_name}"? Properties paying to it will be reset.`)) return;
    try {
      await api.delete(`/landlords/${landlord.user_id}/bank-accounts/${account.account_id}`);
      toast.success("Bank account deleted"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const setPayout = async (property_id, account_id) => {
    setPayoutBusy(property_id);
    try {
      await api.patch(`/properties/${property_id}/payout-account`, { payout_bank_account_id: account_id || null });
      toast.success("Payout account updated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update payout account"); }
    finally { setPayoutBusy(null); }
  };

  const openEdit = (p, landlordUserId) => {
    setEditForm({ reference: p.reference || "", address: p.address || "", postcode: p.postcode || "", city: p.city || "", beds: p.beds ?? "", baths: p.baths ?? "", rent_pcm: p.rent_pcm ?? "", landlord_user_id: landlordUserId || "" });
    setPropEdit({ ...p, _origLandlord: landlordUserId || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await api.patch(`/properties/${propEdit.property_id}/details`, {
        reference: editForm.reference, address: editForm.address, postcode: editForm.postcode, city: editForm.city,
        beds: editForm.beds === "" ? null : parseInt(editForm.beds),
        baths: editForm.baths === "" ? null : parseInt(editForm.baths),
        rent_pcm: editForm.rent_pcm === "" ? null : parseFloat(editForm.rent_pcm),
      });
      if (editForm.landlord_user_id && editForm.landlord_user_id !== propEdit._origLandlord) {
        await api.patch(`/properties/${propEdit.property_id}/landlord`, { landlord_user_id: editForm.landlord_user_id });
        toast.success("Property moved to the selected landlord");
      } else {
        toast.success("Property updated");
      }
      setPropEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update property"); }
    finally { setSavingEdit(false); }
  };

  const toggleProp = (llId, pid) => setSelProps((s) => {
    const cur = s[llId] || [];
    return { ...s, [llId]: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid] };
  });
  const setDate = (llId, key, val) => setStmtDates((d) => ({ ...d, [llId]: { ...d[llId], [key]: val } }));

  const generateCombined = async (l) => {
    const ids = selProps[l.user_id] || [];
    if (ids.length === 0) return;
    setCombBusy(l.user_id);
    try {
      const d = stmtDates[l.user_id] || {};
      const res = await api.post("/statements/combined",
        { property_ids: ids, from_date: d.from || null, to_date: d.to || null }, { responseType: "blob" });
      showBankWarning(res);
      let fname = "Combined Statement.pdf";
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      if (m) fname = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Combined statement for ${ids.length} propert${ids.length === 1 ? "y" : "ies"} downloaded`);
      setSelProps((s) => ({ ...s, [l.user_id]: [] }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate combined statement"); }
    finally { setCombBusy(null); }
  };

  const emailCombined = async (l) => {
    const ids = selProps[l.user_id] || [];
    if (ids.length === 0) return;
    if (!l.email) { toast.error("This landlord has no email address on file."); return; }
    if (!window.confirm(`Email a combined statement for ${ids.length} propert${ids.length === 1 ? "y" : "ies"} to ${l.name} at ${l.email}?`)) return;
    setCombEmailBusy(l.user_id);
    try {
      const d = stmtDates[l.user_id] || {};
      const { data } = await api.post("/statements/combined/email",
        { property_ids: ids, from_date: d.from || null, to_date: d.to || null });
      if (data.emailed) { toast.success(`Combined statement ${data.statement_no} emailed to ${data.email}`); setSelProps((s) => ({ ...s, [l.user_id]: [] })); }
      else toast.warning(`Statement generated, but email is not configured yet (${data.reason || "email disabled"}). It will send once Resend is set up on your live domain.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not email combined statement"); }
    finally { setCombEmailBusy(null); }
  };

  const load = () => api.get("/landlords").then(({ data }) => {
    setData(data);
    const f = {};
    data.landlords.forEach((l) => l.properties.forEach((p) => {
      f[p.property_id] = {
        management_fee_pct: p.management_fee_pct ?? "",
        maintenance_fee_pct: p.maintenance_fee_pct ?? "",
      };
    }));
    setFees(f);
  });
  useEffect(() => { load(); }, []);
  if (!data) return <Layout title="Landlords"><Loading /></Layout>;

  const term = q.trim().toLowerCase();
  const shown = term.length < 2 ? data.landlords : data.landlords.map((l) => {
    const llMatch = (l.name || "").toLowerCase().includes(term) || (l.landlord_name || "").toLowerCase().includes(term) || (l.email || "").toLowerCase().includes(term);
    const matchedProps = l.properties.filter((p) => (p.address || "").toLowerCase().includes(term) || (p.reference || "").toLowerCase().includes(term));
    if (llMatch) return l;
    if (matchedProps.length) return { ...l, properties: matchedProps };
    return null;
  }).filter(Boolean);

  const saveLandlord = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/users", { ...llForm, role: "landlord" });
      toast.success("Landlord added");
      setAddLL(false); setLlForm({ nickname: "", name: "", email: "", phone: "" });
      if (data.credentials) setCreds({ ...data.credentials, role: "landlord" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add landlord"); }
    finally { setBusy(false); }
  };

  const resetLandlordLogin = async (l) => {
    setResetBusy(l.user_id);
    try {
      const { data } = await api.post(`/users/${l.user_id}/reset-password`);
      setCreds({ ...data, role: "landlord" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset login"); }
    finally { setResetBusy(null); }
  };

  const saveProperty = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/properties", {
        ...propForm, rent_pcm: parseFloat(propForm.rent_pcm) || 0,
        beds: parseInt(propForm.beds) || 0, baths: parseInt(propForm.baths) || 0,
        landlord: propForm.registered_name || addProp.landlord_name, landlord_user_id: addProp.user_id,
      });
      toast.success("Property added");
      setAddProp(null); setPropForm({ registered_name: "", reference: "", address: "", postcode: "", city: "", beds: 2, baths: 1, rent_pcm: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add property"); }
    finally { setBusy(false); }
  };

  const saveFees = async (propId) => {
    const f = fees[propId];
    await api.patch(`/properties/${propId}/fees`, {
      management_fee_pct: f.management_fee_pct === "" ? null : parseFloat(f.management_fee_pct),
      maintenance_fee_pct: f.maintenance_fee_pct === "" ? null : parseFloat(f.maintenance_fee_pct),
    });
    toast.success("Fees saved");
    load();
  };

  const deleteProperty = async (p) => {
    if (!window.confirm(`Archive property ${p.address}?\n\nIt will be kept in the archive for 7 years and then automatically deleted. You can restore it any time before then.`)) return;
    try {
      const { data } = await api.delete(`/properties/${p.property_id}`);
      toast.success(data.message || `${p.address} archived`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not archive property"); }
  };

  const restoreProperty = async (p) => {
    try {
      await api.post(`/properties/${p.property_id}/restore`);
      toast.success(`${p.address} restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not restore property"); }
  };

  const downloadStatement = async (l) => {
    setStmtBusy(l.user_id);
    try {
      const res = await api.get(`/landlords/${l.user_id}/statement`, { responseType: "blob" });
      showBankWarning(res);
      let fname = "Combined Statement.pdf";
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m) fname = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${l.name} downloaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate statement"); }
    finally { setStmtBusy(null); }
  };

  const emailStatement = async (l) => {
    if (!l.email) { toast.error("This landlord has no email address on file."); return; }
    if (!window.confirm(`Email the latest statement to ${l.name} at ${l.email}?`)) return;
    setEmailBusy(l.user_id);
    try {
      const { data } = await api.post(`/landlords/${l.user_id}/statement/email`, {});
      if (data.emailed) toast.success(`Statement ${data.statement_no} emailed to ${data.email}`);
      else toast.warning(`Statement generated, but email is not configured yet (${data.reason || "email disabled"}). It will send once Resend is set up on your live domain.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not email statement"); }
    finally { setEmailBusy(null); }
  };

  return (
    <Layout title="Landlords" subtitle="Landlord Accounts & Pricing">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="landlord-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by landlord, company or property address…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <p className="text-sm text-slate-500">Company defaults: <b>{data.default_management_fee_pct}%</b> mgmt · <b>{data.default_maintenance_fee_pct}%</b> maint (Settings)</p>
        <button data-testid="add-landlord-btn" onClick={() => setAddLL(true)} className="ml-auto bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-1.5"><Plus size={15} /> New Landlord</button>
      </div>

      <div className="space-y-5">
        {shown.length === 0 && <Card className="p-10 text-center text-slate-400 text-sm">{term ? "No landlords match your search." : "No landlords yet."}</Card>}
        {shown.map((l) => (
          <Card key={l.user_id} className="p-5 fade-up" data-testid={`landlord-${l.user_id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-semibold">{l.name[0]}</div>
                <div>
                  <p className="font-semibold text-slate-900">{l.name}{l.nickname && l.nickname !== l.name ? <span className="ml-2 text-xs font-normal text-slate-400">“{l.nickname}”</span> : null}</p>
                  <p className="text-xs text-slate-400">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`ll-login-${l.user_id}`} onClick={() => resetLandlordLogin(l)} disabled={resetBusy === l.user_id}
                  className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60" title="Show / reset portal login">
                  {resetBusy === l.user_id ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Portal login
                </button>
                {l.properties.length > 0 && (
                  <button data-testid={`statement-${l.user_id}`} onClick={() => downloadStatement(l)} disabled={stmtBusy === l.user_id}
                    className="border border-blue-200 text-blue-700 hover:bg-blue-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
                    {stmtBusy === l.user_id ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Statement
                  </button>
                )}
                {l.properties.length > 0 && (
                  <button data-testid={`email-statement-${l.user_id}`} onClick={() => emailStatement(l)} disabled={emailBusy === l.user_id || !l.email}
                    title={l.email ? `Email statement to ${l.email}` : "Add an email address for this landlord first"}
                    className="border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
                    {emailBusy === l.user_id ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email statement
                  </button>
                )}
                <button data-testid={`add-property-${l.user_id}`} onClick={() => setAddProp(l)} className="border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg px-3 py-1.5 text-sm font-medium flex items-center gap-1.5"><Plus size={14} /> Add Property</button>
              </div>
            </div>
            {l.properties.filter((p) => !p.archived).length === 0 ? (
              <p className="text-sm text-slate-400">No properties linked yet.</p>
            ) : (
              <div className="space-y-2">
                {l.properties.filter((p) => !p.archived).map((p) => (
                  <div key={p.property_id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50/60" data-testid={`ll-prop-${p.reference}`}>
                    <input type="checkbox" data-testid={`select-prop-${p.reference}`} checked={(selProps[l.user_id] || []).includes(p.property_id)} onChange={() => toggleProp(l.user_id, p.property_id)} className="w-4 h-4 accent-blue-700 cursor-pointer" />
                    <Building2 size={16} className="text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.address}</p>
                      <p className="text-xs text-slate-400">{gbp(p.rent_pcm)}/mo</p>
                    </div>
                    <div className="flex items-end gap-2">
                      <label className="text-[11px] text-slate-500">Payout to
                        <select data-testid={`payout-account-${p.reference}`} value={p.payout_bank_account_id || ""} disabled={payoutBusy === p.property_id}
                          onChange={(e) => setPayout(p.property_id, e.target.value)}
                          className="block w-36 mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:opacity-60">
                          <option value="">{(l.bank_accounts || []).length ? "— None —" : "No accounts"}</option>
                          {(l.bank_accounts || []).map((a) => <option key={a.account_id} value={a.account_id}>{a.label || a.account_name || "Account"}</option>)}
                        </select>
                      </label>
                      <label className="text-[11px] text-slate-500">Mgmt %
                        <input data-testid={`mgmt-fee-${p.reference}`} type="number" step="0.5" min="0" placeholder={`${data.default_management_fee_pct}`} value={fees[p.property_id]?.management_fee_pct ?? ""}
                          onChange={(e) => setFees({ ...fees, [p.property_id]: { ...fees[p.property_id], management_fee_pct: e.target.value } })}
                          className="block w-20 mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      </label>
                      <label className="text-[11px] text-slate-500">Maint %
                        <input data-testid={`maint-fee-${p.reference}`} type="number" step="0.5" min="0" placeholder={`${data.default_maintenance_fee_pct}`} value={fees[p.property_id]?.maintenance_fee_pct ?? ""}
                          onChange={(e) => setFees({ ...fees, [p.property_id]: { ...fees[p.property_id], maintenance_fee_pct: e.target.value } })}
                          className="block w-20 mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                      </label>
                      <button data-testid={`save-fee-${p.reference}`} onClick={() => saveFees(p.property_id)} className="h-8 px-3 rounded-md bg-blue-700 text-white hover:bg-blue-800 text-sm flex items-center gap-1"><Save size={13} /> Save</button>
                      <button data-testid={`edit-prop-${p.reference}`} onClick={() => openEdit(p, l.user_id)} title="Edit property details" className="h-8 w-8 rounded-md border border-slate-300 text-slate-600 hover:bg-white flex items-center justify-center"><Pencil size={14} /></button>
                      <button data-testid={`delete-prop-${p.reference}`} onClick={() => deleteProperty(p)} title="Archive property (kept 7 years)" className="h-8 w-8 rounded-md border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center"><Trash2 size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 pt-3 border-t border-dashed border-slate-200" data-testid={`bank-section-${l.user_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><Landmark size={12} /> Bank accounts</p>
                <button data-testid={`add-bank-${l.user_id}`} onClick={() => openBank(l)} className="text-xs text-blue-700 hover:text-blue-900 font-medium flex items-center gap-1"><Plus size={12} /> Add account</button>
              </div>
              {(l.bank_accounts || []).length === 0 ? (
                <p className="text-xs text-slate-400">No bank accounts on file. Add one so property payouts can be tracked on statements.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {(l.bank_accounts || []).map((a) => (
                    <div key={a.account_id} className="flex items-start gap-2 p-2.5 rounded-lg border border-slate-100 bg-white" data-testid={`bank-acct-${a.account_id}`}>
                      <CreditCard size={15} className="text-slate-400 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{a.label || a.account_name || "Account"}</p>
                        <p className="text-xs text-slate-500 truncate">{a.account_name}{a.sort_code ? ` · ${a.sort_code}` : ""}{a.account_number ? ` · ${a.account_number}` : ""}</p>
                        {a.notes && <p className="text-[11px] text-slate-400 truncate">{a.notes}</p>}
                      </div>
                      <button data-testid={`edit-bank-${a.account_id}`} onClick={() => openBank(l, a)} title="Edit" className="text-slate-400 hover:text-blue-700"><Pencil size={13} /></button>
                      <button data-testid={`delete-bank-${a.account_id}`} onClick={() => deleteBank(l, a)} title="Delete" className="text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {l.properties.filter((p) => p.archived).length > 0 && (
              <div className="mt-4 pt-3 border-t border-dashed border-slate-200" data-testid={`archived-section-${l.user_id}`}>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Archive size={12} /> Archived (auto-deletes after 7 years)</p>
                <div className="space-y-2">
                  {l.properties.filter((p) => p.archived).map((p) => (
                    <div key={p.property_id} className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-100/70 opacity-80" data-testid={`archived-prop-${p.reference}`}>
                      <Archive size={15} className="text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-500 truncate line-through">{p.address}</p>
                        <p className="text-[11px] text-slate-400">Auto-deletes {p.auto_delete_at ? new Date(p.auto_delete_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "in 7 years"}</p>
                      </div>
                      <button data-testid={`restore-prop-${p.reference}`} onClick={() => restoreProperty(p)} className="h-8 px-3 rounded-md border border-slate-300 text-slate-600 hover:bg-white text-xs font-medium flex items-center gap-1.5"><RotateCcw size={13} /> Restore</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(selProps[l.user_id] || []).length > 0 && (
              <div className="mt-3 flex flex-wrap items-end gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 fade-up" data-testid={`combine-bar-${l.user_id}`}>
                <span className="text-sm font-medium text-blue-900">{(selProps[l.user_id] || []).length} selected</span>
                <label className="text-[11px] text-slate-500">From
                  <input type="date" data-testid={`combine-from-${l.user_id}`} value={(stmtDates[l.user_id] || {}).from || ""} onChange={(e) => setDate(l.user_id, "from", e.target.value)}
                    className="block mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </label>
                <label className="text-[11px] text-slate-500">To
                  <input type="date" data-testid={`combine-to-${l.user_id}`} value={(stmtDates[l.user_id] || {}).to || ""} onChange={(e) => setDate(l.user_id, "to", e.target.value)}
                    className="block mt-0.5 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                </label>
                <button data-testid={`generate-combined-${l.user_id}`} onClick={() => generateCombined(l)} disabled={combBusy === l.user_id}
                  className="ml-auto bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-60">
                  {combBusy === l.user_id ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Generate combined statement
                </button>
                <button data-testid={`email-combined-${l.user_id}`} onClick={() => emailCombined(l)} disabled={combEmailBusy === l.user_id || !l.email}
                  title={l.email ? `Email combined statement to ${l.email}` : "Add an email address for this landlord first"}
                  className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50">
                  {combEmailBusy === l.user_id ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email combined
                </button>
                <button onClick={() => setSelProps((s) => ({ ...s, [l.user_id]: [] }))} className="text-xs text-slate-500 hover:text-slate-800 underline">Clear</button>
              </div>
            )}
          </Card>
        ))}
      </div>

      <CredentialsDialog creds={creds} title="Landlord portal login" onClose={() => setCreds(null)} />

      {addLL && (
        <Modal title="New Landlord" onClose={() => setAddLL(false)}>
          <form onSubmit={saveLandlord} className="space-y-3">
            <Field label="Nickname" testid="ll-nickname" value={llForm.nickname} required onChange={(v) => setLlForm({ ...llForm, nickname: v, name: v })} placeholder="e.g. Smith — Oakfield" />
            <Field label="Email" testid="ll-email" type="email" value={llForm.email} onChange={(v) => setLlForm({ ...llForm, email: v })} required />
            <Field label="Phone" testid="ll-phone" value={llForm.phone} onChange={(v) => setLlForm({ ...llForm, phone: v })} />
            <p className="text-xs text-slate-400">A temporary password is generated automatically and emailed to the landlord — you'll also see it on screen after adding.</p>
            <SubmitBtn busy={busy} testid="save-landlord-btn">Add Landlord</SubmitBtn>
          </form>
        </Modal>
      )}

      {addProp && (
        <Modal title={`Add Property — ${addProp.name}`} onClose={() => setAddProp(null)}>
          <form onSubmit={saveProperty} className="space-y-3">
            <Field label="Registered name" testid="prop-registered-name" value={propForm.registered_name} onChange={(v) => setPropForm({ ...propForm, registered_name: v })} required placeholder="Company name or personal name" />
            <Field label="Reference" testid="prop-ref" value={propForm.reference} onChange={(v) => setPropForm({ ...propForm, reference: v })} required />
            <AddressAutocomplete label="Address" testid="prop-address" value={propForm.address} required
              onChange={(v) => setPropForm({ ...propForm, address: v })}
              onSelect={({ address, city, postcode }) => setPropForm((f) => ({ ...f, address, city: city || f.city, postcode: postcode || f.postcode }))} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Postcode" testid="prop-postcode" value={propForm.postcode} onChange={(v) => setPropForm({ ...propForm, postcode: v })} />
              <Field label="City" testid="prop-city" value={propForm.city} onChange={(v) => setPropForm({ ...propForm, city: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Beds" testid="prop-beds" type="number" value={propForm.beds} onChange={(v) => setPropForm({ ...propForm, beds: v })} />
              <Field label="Baths" testid="prop-baths" type="number" value={propForm.baths} onChange={(v) => setPropForm({ ...propForm, baths: v })} />
              <Field label="Rent pcm" testid="prop-rent" type="number" value={propForm.rent_pcm} onChange={(v) => setPropForm({ ...propForm, rent_pcm: v })} />
            </div>
            <SubmitBtn busy={busy} testid="save-property-btn">Add Property</SubmitBtn>
          </form>
        </Modal>
      )}

      {bankModal && (
        <Modal title={`${bankModal.account ? "Edit" : "Add"} Bank Account — ${bankModal.landlord.name}`} onClose={() => setBankModal(null)}>
          <form onSubmit={saveBank} className="space-y-3">
            <Field label="Label / nickname" testid="bank-label" value={bankForm.label} onChange={(v) => setBankForm({ ...bankForm, label: v })} placeholder="e.g. Main account" />
            <Field label="Account holder name" testid="bank-account-name" value={bankForm.account_name} onChange={(v) => setBankForm({ ...bankForm, account_name: v })} required />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sort code" testid="bank-sort-code" value={bankForm.sort_code} onChange={(v) => setBankForm({ ...bankForm, sort_code: v })} placeholder="12-34-56" />
              <Field label="Account number" testid="bank-account-number" value={bankForm.account_number} onChange={(v) => setBankForm({ ...bankForm, account_number: v })} placeholder="12345678" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
              <textarea data-testid="bank-notes" rows={2} value={bankForm.notes} onChange={(e) => setBankForm({ ...bankForm, notes: e.target.value })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="e.g. Building society roll number, reference…" />
            </div>
            <SubmitBtn busy={savingBank} testid="save-bank-btn">{bankModal.account ? "Save changes" : "Add account"}</SubmitBtn>
          </form>
        </Modal>
      )}

      {propEdit && (
        <Modal title={`Edit Property — ${propEdit.address || propEdit.reference}`} onClose={() => setPropEdit(null)}>
          <form onSubmit={saveEdit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Landlord</label>
              <select data-testid="edit-prop-landlord" value={editForm.landlord_user_id} onChange={(e) => setEditForm({ ...editForm, landlord_user_id: e.target.value })}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                {data.landlords.map((ll) => <option key={ll.user_id} value={ll.user_id}>{ll.name}</option>)}
              </select>
              {editForm.landlord_user_id !== propEdit._origLandlord && (
                <p className="text-[11px] text-amber-600 mt-1">This property will be moved to the selected landlord. Its payout account will be reset.</p>
              )}
            </div>
            <Field label="Reference" testid="edit-prop-ref" value={editForm.reference} onChange={(v) => setEditForm({ ...editForm, reference: v })} required />
            <AddressAutocomplete label="Address" testid="edit-prop-address" value={editForm.address} required
              onChange={(v) => setEditForm({ ...editForm, address: v })}
              onSelect={({ address, city, postcode }) => setEditForm((f) => ({ ...f, address, city: city || f.city, postcode: postcode || f.postcode }))} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Postcode" testid="edit-prop-postcode" value={editForm.postcode} onChange={(v) => setEditForm({ ...editForm, postcode: v })} />
              <Field label="City" testid="edit-prop-city" value={editForm.city} onChange={(v) => setEditForm({ ...editForm, city: v })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Beds" testid="edit-prop-beds" type="number" value={editForm.beds} onChange={(v) => setEditForm({ ...editForm, beds: v })} />
              <Field label="Baths" testid="edit-prop-baths" type="number" value={editForm.baths} onChange={(v) => setEditForm({ ...editForm, baths: v })} />
              <Field label="Rent pcm" testid="edit-prop-rent" type="number" value={editForm.rent_pcm} onChange={(v) => setEditForm({ ...editForm, rent_pcm: v })} />
            </div>
            <SubmitBtn busy={savingEdit} testid="save-edit-prop-btn">Save changes</SubmitBtn>
          </form>
        </Modal>
      )}
    </Layout>
  );
}

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl font-bold text-slate-900">{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, testid, value, onChange, type = "text", required, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
    <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
  </div>
);

const SubmitBtn = ({ busy, testid, children }) => (
  <button type="submit" data-testid={testid} disabled={busy} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
    {busy ? <Loader2 size={16} className="animate-spin" /> : null} {children}
  </button>
);
